/**
 * Shared map utilities — used by RMT, FAST Report, MEL, etc.
 * Prefer the backend UN proxy so browser CORS / payload limits do not break one module
 * when another module changes how it loads maps.
 */

export type CountryFeatureProperties = Record<string, string | number | undefined>;

/** Read ISO3 from UN ClearMap GeoJSON (keys may be ISO3CD or iso3cd). */
export const getFeatureIso3 = (feature?: {
  properties?: CountryFeatureProperties;
} | null): string => {
  const props = feature?.properties;
  if (!props) return '';
  const iso = props.ISO3CD ?? props.iso3cd ?? props.ISO3 ?? props.iso3;
  return iso ? String(iso).trim().toUpperCase() : '';
};

/** Read country name from UN ClearMap GeoJSON (ROMNAM / romnam, etc.). */
export const getFeatureCountryName = (feature?: {
  properties?: CountryFeatureProperties;
} | null): string => {
  const props = feature?.properties;
  if (!props) return '';
  const name =
    props.ROMNAM ??
    props.romnam ??
    props.COUNTRY ??
    props.country ??
    props.NAME_0 ??
    props.name ??
    props.NAME ??
    '';
  return name ? String(name) : '';
};

/** Ensure uppercase ISO3CD / ROMNAM aliases exist (UN often returns lowercase keys). */
const normalizeFeatureProperties = (feature: any) => {
  if (!feature?.properties || typeof feature.properties !== 'object') return feature;
  const props = feature.properties;
  const iso3 = props.ISO3CD ?? props.iso3cd ?? props.ISO3 ?? props.iso3;
  const name = props.ROMNAM ?? props.romnam ?? props.NAME ?? props.name;
  return {
    ...feature,
    properties: {
      ...props,
      ...(iso3 != null && iso3 !== ''
        ? { ISO3CD: String(iso3).trim().toUpperCase(), iso3cd: String(iso3).trim().toUpperCase() }
        : {}),
      ...(name != null && name !== ''
        ? { ROMNAM: String(name), romnam: String(name) }
        : {}),
    },
  };
};

const normalizeFeatureCollection = (data: any) => {
  if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) {
    return null;
  }
  const features = data.features
    .filter(
      (feature: any) =>
        feature &&
        feature.type === 'Feature' &&
        feature.geometry &&
        feature.properties,
    )
    .map(normalizeFeatureProperties);
  return { type: 'FeatureCollection', features };
};

/**
 * Fetches GeoJSON country boundaries (UN ClearMap layer 109).
 * Tries the Nexus backend proxy first (same path FAST/MEL use), then direct UN API.
 */
export const fetchCountryBoundaries = async (iso3Codes?: string[]) => {
  const uniqueCodes = Array.from(
    new Set((iso3Codes || []).map((c) => String(c).trim().toUpperCase()).filter(Boolean)),
  );

  // Backend proxy — avoids browser CORS / oversized direct UN calls; batches large sets
  if (uniqueCodes.length > 0) {
    try {
      const res = await fetch(
        `/api/fast-report/country-boundaries?iso3=${uniqueCodes.join(',')}`,
      );
      if (res.ok) {
        const data = normalizeFeatureCollection(await res.json());
        if (data && data.features.length > 0) {
          console.log(
            `Successfully loaded ${data.features.length} countries via backend UN proxy`,
          );
          return data;
        }
      }
    } catch (err) {
      console.warn('Backend UN boundaries proxy failed, trying direct UN API', err);
    }
  }

  try {
    const UN_GEOSERVICES_BASE =
      'https://geoservices.un.org/arcgis/rest/services/ClearMap_WebTopo/MapServer/109/query';

    const params = new URLSearchParams({
      // Field names on the service are lowercase; aliases are ISO3CD/ROMNAM
      outFields: 'iso3cd,romnam',
      returnGeometry: 'true',
      outSR: '4326',
      f: 'geojson',
    });

    if (uniqueCodes.length > 0) {
      const codes_str = uniqueCodes.join("','");
      params.set('where', `iso3cd IN ('${codes_str}')`);
    } else {
      params.set('where', '1=1');
    }

    const response = await fetch(`${UN_GEOSERVICES_BASE}?${params}`);

    if (!response.ok) {
      throw new Error(`UN GeoServices API error: ${response.status} ${response.statusText}`);
    }

    const data = normalizeFeatureCollection(await response.json());
    if (!data) {
      throw new Error('Response is not a valid GeoJSON FeatureCollection');
    }

    console.log(
      `Successfully loaded ${data.features.length} valid countries from UN GeoServices`,
    );

    return data;
  } catch (error) {
    console.error('Error fetching country boundaries from UN GeoServices:', error);
    return null;
  }
};

/**
 * Normalizes country names between GeoJSON and API data
 * This helps match country names that might be slightly different
 */
export const normalizeCountryName = (name: string): string => {
  const normalizations: Record<string, string> = {
    'United States of America': 'United States',
    USA: 'United States',
    'United Kingdom of Great Britain and Northern Ireland': 'United Kingdom',
    UK: 'United Kingdom',
    'Russian Federation': 'Russia',
    Macedonia: 'North Macedonia',
    'Republic of Moldova': 'Moldova',
    'Congo, Democratic Republic of the': 'Democratic Republic of the Congo',
    Congo: 'Republic of the Congo',
    Czechia: 'Czech Republic',
    Türkiye: 'Turkey',
  };

  return normalizations[name] || name;
};

/**
 * Calculates a risk color based on a score value (0–3 scale).
 */
export { getRiskColorHex as getRiskColor } from '../riskScoreColors';
